import Papa from 'papaparse'
import {
  HeaderValues,
  FileStats,
  ParseColumnType,
  CsvTemplateType,
} from './csv.constants'

// Headers are matched leniently — trimmed, case-insensitive, and tolerant of a
// leading UTF-8 BOM on the first column — to mirror how cell values are matched
// and to avoid rejecting CSVs that differ only in header casing/whitespace.
export const normalizeHeader = (header: string): string =>
  header
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()

export const transformHeader = (
  header: string,
  headers: HeaderValues[],
  fileStats: FileStats,
  seenHeaders: Set<string>,
  headerErrors: string[],
): string => {
  fileStats.headersLen++
  const normalized = normalizeHeader(header)
  if (seenHeaders.has(normalized)) {
    headerErrors.push(`Header ${header} appears multiple times.`)
    return ''
  } else {
    seenHeaders.add(normalized)
  }
  const expectedHeader = headers.find((candidate) => {
    return normalizeHeader(candidate.input) === normalized
  })
  if (!expectedHeader) {
    headerErrors.push(
      `Header name issue: '${header}' does not match expected headers.`,
    )
    return ''
  }
  return expectedHeader.output
}

export const transformValue = (
  value: string,
  columnName: string,
  fileStats: FileStats,
  columnParsers: ParseColumnType,
  bodyErrors: string[],
): string[] | string => {
  fileStats.currCell++

  if (!(columnName in columnParsers)) {
    // This a dynamically generated column if there are no header errors
    return value
  }
  const validation = columnParsers[columnName].validator(
    columnName,
    value,
    fileStats.row(),
  )
  if (validation !== null && validation.length) {
    if (Array.isArray(validation)) {
      bodyErrors.push(...validation)
      return ''
    } else {
      bodyErrors.push(validation)
      return ''
    }
  }
  return columnParsers[columnName].mutator(value)
}

export const getCSVBlob = (data: CsvTemplateType[]): Blob => {
  const csv = Papa.unparse(data)
  return new Blob([csv], { type: 'text/csv' })
}

/** Removes one pair of surrounding CSV double-quotes and un-escapes "" -> ". */
export const stripOuterCsvQuotes = (value: string): string => {
  const t = value.trim()
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) {
    return t.slice(1, -1).replace(/""/g, '"')
  }
  return t
}

export const separateValues = (value: string): string[] => {
  const values: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < value.length; i++) {
    const char = value[i]
    const nextChar = value[i + 1]

    if (char === '"') {
      // Support escaped quotes in CSV fields: ""
      if (inQuotes && nextChar === '"') {
        current += '"'
        i++
        continue
      }
      inQuotes = !inQuotes
      continue
    }

    if (!inQuotes && (char === ',' || char === '\n' || char === ';')) {
      const trimmed = current.trim()
      if (trimmed !== '') {
        values.push(trimmed)
      }
      current = ''
      continue
    }

    current += char
  }

  const trimmed = current.trim()
  if (trimmed !== '') {
    values.push(trimmed)
  }

  return values
}

/**
 * Escapes values that begin with risky prefixes (=, +, -, @) to prevent
 * spreadsheet formula injection.
 */
export const sanitizeCSVValue = (value: string): string => {
  const riskyPrefixes = ['=', '+', '-', '@']
  if (riskyPrefixes.some((prefix) => value.startsWith(prefix))) {
    return `'${value}`
  }
  return value
}
